import { formatFixed, tt } from '@agos/shared'

/**
 * The Kombo BOX configuration, ported from legacy `lib/burger-box-store.ts`.
 *
 * The backend stores this as an opaque JSON blob — `GET /burger-box-config`
 * answers `JsonNode` — so this file is the only definition of the shape that
 * exists anywhere. That is true in legacy too, which is why it lives with the
 * module rather than in `packages/api-client`'s stopgap contracts: it is
 * genuinely frontend-owned state, not a backend contract being guessed at.
 */

export interface SlotPresetItem {
  productId: number
  /** Always included rather than chosen by the customer. */
  fixed: boolean
}

export interface BurgerBoxSlot {
  id: string
  label: string
  categoryId: number | null
  required: boolean
  presetItems: SlotPresetItem[]
  imageUrl?: string | null
}

export interface BurgerBoxConfig {
  active: boolean
  name: string
  description: string
  /** Open this category on the customer site and the box is offered. */
  triggerCategoryId: number | null
  hideProductImages?: boolean
  /**
   * Whether the box opens collapsed on the customer site - §9.646, owner O37.
   *
   * Optional, and **absent means collapsed**: the owner asked for *"collapsed by default"*, so a box nobody has
   * configured opens closed and the switch is how a franchise says otherwise.
   */
  collapsedByDefault?: boolean
  /**
   * Sold for this, whatever its parts cost - the owner's item E, §10A.107.
   *
   * *"ak dám že stojí 7.50 tak bude stáť toľko"*. **The server is what applies it**: every slot
   * carries a real `productId`, so `ServerPricing` re-reads each from the catalogue and a price this
   * screen sent would be displayed and not billed. What this field does is tell the server; the
   * number here is never the authority for what anybody pays.
   *
   * `null` or absent means *price it as the sum of its parts*, which is every box today. **Zero means
   * the same**, because in a free-form blob they cannot be told apart - a free box is a gift rule.
   */
  fixedPrice?: number | null
  slots: BurgerBoxSlot[]
}

export type BurgerBoxItem = BurgerBoxConfig & { id: string }

export function defaultSlots(): BurgerBoxSlot[] {
  return [
    { id: 'burger', label: 'Burger', categoryId: null, required: true, presetItems: [], imageUrl: null },
    { id: 'priloha', label: tt('Príloha'), categoryId: null, required: false, presetItems: [], imageUrl: null },
    { id: 'dressing', label: 'Dressing', categoryId: null, required: false, presetItems: [], imageUrl: null },
    { id: 'napoj', label: tt('Nápoj'), categoryId: null, required: false, presetItems: [], imageUrl: null },
  ]
}

export function newBurgerBox(): BurgerBoxItem {
  return {
    // FB-020 note: legacy uses Date.now() here; two boxes made in the same
    // millisecond would share a React key. Kept for now — changing it changes
    // stored data. See the spec's improvement proposals.
    id: `box_${Date.now()}`,
    active: true,
    name: tt('Nový Kombo BOX'),
    description: '',
    triggerCategoryId: null,
    slots: defaultSlots(),
  }
}

/**
 * Reads whatever the endpoint answered into the current shape.
 *
 * Two historical forms are accepted: an array of boxes, and a bare object from
 * before a franchise could have more than one. Slots saved before presets
 * existed have no `presetItems`, so it is defaulted. A franchise still on the
 * old shape must not lose its box.
 */
export function parseBurgerBoxConfig(payload: unknown): BurgerBoxItem[] {
  /**
   * Slots are read as partial on purpose: ones saved before presets existed
   * have no `presetItems`, and the stored blob is not validated by anything.
   */
  type StoredBox = Omit<Partial<BurgerBoxItem>, 'slots'> & { slots?: Partial<BurgerBoxSlot>[] }

  const withSlotDefaults = (box: StoredBox, id: string): BurgerBoxItem => ({
    active: true,
    name: '',
    description: '',
    triggerCategoryId: null,
    fixedPrice: null,
    ...box,
    id: box.id ?? id,
    slots: (box.slots ?? []).map((slot, index) => ({
      id: slot.id ?? `slot_${index}`,
      label: slot.label ?? '',
      categoryId: slot.categoryId ?? null,
      required: slot.required ?? false,
      presetItems: slot.presetItems ?? [],
      imageUrl: slot.imageUrl ?? null,
    })),
  })

  if (Array.isArray(payload)) {
    return (payload as StoredBox[]).map((box, index) =>
      withSlotDefaults(box, `box_${index}`),
    )
  }

  if (payload && typeof payload === 'object' && !('error' in payload)) {
    const single = payload as StoredBox
    if (Object.keys(single).length === 0) return []
    return [withSlotDefaults(single, 'box_legacy')]
  }

  return []
}

/**
 * The fixed price as the server will read it, or `null` - §10A.107.
 *
 * **Kept out of the payload entirely when it is not set**, rather than sent as `null`: the stored blob
 * is what the server parses, and a box that never had a fixed price should not grow a field saying it
 * has none. A value of zero is *not set*, for the reason on the field itself.
 *
 * Accepts a comma as well as a point, because a Slovak keyboard produces a comma and the owner types
 * *7,50*. That is the same rule every other money field on the manager applies.
 */
export function fixedPriceOf(raw: string): number | null {
  const typed = raw.trim().replace(',', '.')
  if (typed === '') return null
  const value = Number(typed)
  if (!Number.isFinite(value) || value <= 0) return null
  // Two places, because that is what a price column holds and what the server will read.
  return Math.round(value * 100) / 100
}

/**
 * What the field shows for a stored value. Empty for *not set*, so the box reads as unpriced.
 *
 * `formatFixed` rather than `toFixed`, which writes *7.50* on a screen that reads *7,50* - the lint
 * rule that caught this is FRONTEND-BUGS.md N-02, and it is right: the owner types a comma, and a
 * field that shows a dot while `fixedPriceOf` accepts a comma would be telling them their own input
 * was wrong.
 */
export function fixedPriceText(value: number | null | undefined): string {
  if (value === null || value === undefined || value <= 0) return ''
  return formatFixed(value, 2)
}
